// Insights
//
// CS-rep oriented dashboard. Loads referrals + vitalrounds + logins +
// messages for the selected window (and the equivalent prior window), then
// rolls them up into a grid of "insight cards" — leader boards, trend
// callouts, mix breakdowns. Same controls strip as Analytics / Collab /
// Quick Refer for consistency.
//
// All aggregation is client-side useMemo over the loaded records. Cards
// render gracefully empty when a data type isn't loaded (e.g. logins
// haven't been pulled into the cache yet).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import FacilityFilter from '../components/FacilityFilter';
import client from '../api/client';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import { isTestRecord, isTestFacilityName } from '../utils/testDataFilter';
import PersonContactHover from '../components/PersonContactHover';
import { PersonContactsProvider } from '../contexts/PersonContactsContext';
import './Insights.css';
import './CollaborationAnalytics.css'; // bar list / pill / table look

const TOP_N = 10;

// Tab definitions for the categorized layout. Order = display order in the
// tab bar. Actions sits at the end as the "do something with what you just
// learned" capstone — it pulls every drop-off / rising / declining /
// growing row from the other tabs and shows the AI-suggested next step
// for each, in one consolidated to-do list.
const TABS = [
  { id: 'people',      label: 'People',      hint: 'Top users + drop-off / rising stars' },
  { id: 'facilities',  label: 'Facilities',  hint: 'Sending, receiving, growth, completion' },
  { id: 'specialties', label: 'Specialties', hint: 'Top specialties + referral reasons' },
  { id: 'spokes',      label: 'Spokes',      hint: 'Non-customer (free-account) activity' },
  { id: 'actions',     label: 'Actions',     hint: 'AI-suggested next steps for every trend row' },
];

// Per-groupKey tone for the Actions tab badge. The AI echoes one of
// these as `groupKey`; we map it to a colored chip so the user can tell
// at a glance whether an action is about a churn risk (red) or growth
// opportunity (green).
const GROUP_TONE = {
  'users-dropping':              'bad',
  'users-rising':                'good',
  'facilities-declining':        'bad',
  'facilities-growing':          'good',
  'spoke-users-dropping':        'bad',
  'spoke-users-rising':          'good',
  'spoke-facilities-declining':  'bad',
  'spoke-facilities-growing':    'good',
};

const f = (r, k) => (r?.data || r?.Data || {})[k];

// Activity-counter sums per record, mirroring DataChart's ROLLUP_ACTIVITY_FIELDS.
const LOGIN_FIELDS = [
  'web_login', 'goweb_login', 'common_login',
  'desktop_login', 'mobile', 'telemedicine_login',
];
// "Sent" buckets only — matches the Messages metric used elsewhere.
const MESSAGE_SENT_FIELDS = [
  'referral_messages_sent', 'vmail_messages_sent', 'vmail_messages_Sent',
  'vchat_messages_sent', 'vchat_messages_Sent',
  'vround_messages_sent', 'vtransfer_messages_sent', 'Other_messages_sent',
];

function sumFields(record, fields) {
  let total = 0;
  const data = record?.data || record?.Data || {};
  for (const key of fields) {
    const v = Number(data[key]);
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

// Same casing-tolerant lookup pattern: try a couple variants.
function pickInt(record, ...keys) {
  const data = record?.data || record?.Data || {};
  for (const k of keys) {
    const v = data[k];
    if (v !== undefined && v !== null) return Number(v);
  }
  return 0;
}

const COMPLETED_RX = /complete|closed|accepted|finished/i;

// Substrings that identify VitalEngine internal staff / facilities. Used by
// the optional "Remove VitalEngine users" toggle, which is independent of
// "Remove Test Data" so CS reps can keep test referrals visible while still
// filtering VE staff out of activity leaderboards.
const VE_FACILITY_RX = /vitalengine|ve[\s-]?engineering|myheart/i;
const VE_NAME_RX     = /vitalengine|ve[\s-]?engineering/i;
// Quick Refer placeholder for CMS-NPI physicians not yet signed up — not VE staff.
const NON_VE_PLACEHOLDER_FACILITY = /^non\s+vitalengine\s+user\s+facility$/i;

function isVeFacilityLabel(name) {
  if (!name) return false;
  if (NON_VE_PLACEHOLDER_FACILITY.test(String(name).trim())) return false;
  return VE_FACILITY_RX.test(name);
}

function isVitalEngineRow(record) {
  const sendFac    = f(record, 'sending_facility');
  const recvFac    = f(record, 'receiving_facility');
  const createFac  = f(record, 'creating_facility');
  const sendSpec   = f(record, 'sending_speciality');
  const recvSpec   = f(record, 'receiving_speciality');
  const createSpec = f(record, 'creating_speciality');
  const sendName   = f(record, 'sending_user_name');
  const recvName   = f(record, 'receiving_user_name');
  const createName = f(record, 'creating_user_name');
  const facName    = f(record, 'facility_name');
  const userName   = f(record, 'user_name');
  return (
    isVeFacilityLabel(facName)                      ||
    isVeFacilityLabel(sendFac)                      ||
    isVeFacilityLabel(recvFac)                      ||
    isVeFacilityLabel(createFac)                    ||
    (sendSpec   && VE_NAME_RX.test(sendSpec))       ||
    (recvSpec   && VE_NAME_RX.test(recvSpec))       ||
    (createSpec && VE_NAME_RX.test(createSpec))     ||
    (sendName   && VE_NAME_RX.test(sendName))       ||
    (recvName   && VE_NAME_RX.test(recvName))       ||
    (createName && VE_NAME_RX.test(createName))     ||
    (userName   && VE_NAME_RX.test(userName))
  );
}

// All "last X month/year" presets use FULL-MONTH boundaries — the in-progress
// month is excluded so a partial month doesn't drag the period averages
// down. Today=May 6 → "Last 3 Months" returns Feb 1 – Apr 30, NOT Feb 6 –
// May 5. (The comparison logic on Insights' trend cards then uses an
// equally-sized prior window ending the day before this window starts.)
//
// "Custom Range" is the escape hatch for users who want fractional / arbitrary
// dates.
function calculateDateRange(preset) {
  const today = new Date();
  // End-of-last-full-month: day-0 of this month = last day of previous month.
  const endOfLastFullMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  // start at first-of-some-prior-month; exact month picked per preset below.
  const monthsBack = (n) =>
    new Date(endOfLastFullMonth.getFullYear(), endOfLastFullMonth.getMonth() - (n - 1), 1);
  switch (preset) {
    case 'last_month':     return { start: monthsBack(1),  end: endOfLastFullMonth };
    case 'last_3_months':  return { start: monthsBack(3),  end: endOfLastFullMonth };
    case 'last_6_months':  return { start: monthsBack(6),  end: endOfLastFullMonth };
    case 'last_year':      return { start: monthsBack(12), end: endOfLastFullMonth };
    case 'last_18_months': return { start: monthsBack(18), end: endOfLastFullMonth };
    case 'last_2_years':   return { start: monthsBack(24), end: endOfLastFullMonth };
    default:               return { start: today, end: today };
  }
}
const formatDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Compute the "previous" window — same length as [start, end], ending the
// day before `start`. Used for trend / drop-off cards.
function previousWindow(startStr, endStr) {
  const s = new Date(startStr);
  const e = new Date(endStr);
  const lenMs = e - s;
  const prevEnd = new Date(s);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd.getTime() - lenMs);
  return { prevStart: formatDate(prevStart), prevEnd: formatDate(prevEnd) };
}

function Insights() {
  const cache = useCache();

  const [dateRangePreset, setDateRangePreset] = useState('last_3_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [removeTestData, setRemoveTestData] = useState(true);
  const [removeVitalEngineUsers, setRemoveVitalEngineUsers] = useState(false);

  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');

  const { customers, resolveCustomerFacilityIds, allCustomerFacilityIds } = useCustomerFilter();
  const [activeCustomerId, setActiveCustomerId] = useState('');

  // Compact filter strip vs full-form view.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  // Cross-page scope (drives navigation: scope set on this page sticks
  // across pages until cleared). The Clear button on the green Viewing
  // bar must wipe this too, otherwise navigating to another page would
  // re-load the cleared scope from sessionStorage.
  const { clearScope: clearGlobalScope } = useScope();

  // Single source of truth for "what is the user analyzing" so the
  // page title and the green scope bar stay in sync regardless of
  // which scope source the user used.
  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  // Human label for the date-range preset shown in the compact strip.
  // Insights is unique: month-named presets are full-months only, so the
  // strip notes that explicitly to avoid the "Last Year (12 full months)"
  // semantic getting lost when collapsed.
  const dateRangeLabel = useMemo(() => {
    const presetNames = {
      last_month: 'Last Month',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last 12 Months',
      last_18_months: 'Last 18 Months',
      last_2_years: 'Last 2 Years',
      custom: 'Custom Range',
    };
    const base = presetNames[dateRangePreset] || 'Date Range';
    if (startDate && endDate) {
      return `${base} (${startDate} \u2192 ${endDate})`;
    }
    return base;
  }, [dateRangePreset, startDate, endDate]);

  // Tab state for the new categorized layout. Persists in sessionStorage so
  // hopping to another page and back doesn't drop the user out of the
  // Spokes tab they were just inspecting.
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('insights-active-tab') || 'people';
  });
  useEffect(() => {
    sessionStorage.setItem('insights-active-tab', activeTab);
  }, [activeTab]);

  // Per-data-type loading state — surfaces which feeds came back.
  const [referrals, setReferrals] = useState([]);
  const [vrounds,   setVrounds]   = useState([]);
  const [logins,    setLogins]    = useState([]);
  const [messages,  setMessages]  = useState([]);

  const [prevReferrals, setPrevReferrals] = useState([]);
  const [prevLogins,    setPrevLogins]    = useState([]);
  const [prevMessages,  setPrevMessages]  = useState([]);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {current, total, label}
  const [errors, setErrors] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r.success) setFacilityHierarchy(r.hierarchy || []);
      } catch { /* hierarchy may not be cached yet */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet */ }
    })();
    const { start, end } = calculateDateRange('last_3_months');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = calculateDateRange(dateRangePreset);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRangePreset]);

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) { setSelectedFacilityIds(new Set()); }
    else {
      const sf = savedFilters.find(s => s.id === parseInt(filterId, 10));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds));
    }
    // Mirror the user's pick into the global StickyScopeBar.
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) { setSelectedFacilityIds(new Set()); }
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids));
    }
    setScopeFromCustomer(customerId);
  };

  // Fetch a single data type, swallowing 404 / not-cached errors so the
  // page degrades gracefully if (e.g.) logins haven't been pulled yet.
  const fetchOne = useCallback(async (label, type, s, e) => {
    try {
      const res = await cache.getCachedData(type, s, e);
      return res?.success ? (res.records || []) : [];
    } catch (err) {
      setErrors(prev => [...prev, `${label}: ${err.response?.data?.error || err.message}`]);
      return [];
    }
  }, [cache]);

  const loadAll = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrors([]);
    setHasLoaded(false);

    const { prevStart, prevEnd } = previousWindow(startDate, endDate);
    const tasks = [
      ['Referrals (current)',   'referrals',   startDate, endDate, setReferrals],
      ['VitalRounds (current)', 'vitalrounds', startDate, endDate, setVrounds],
      ['Logins (current)',      'logins',      startDate, endDate, setLogins],
      ['Messages (current)',    'messages',    startDate, endDate, setMessages],
      ['Referrals (prior)',     'referrals',   prevStart, prevEnd, setPrevReferrals],
      ['Logins (prior)',        'logins',      prevStart, prevEnd, setPrevLogins],
      ['Messages (prior)',      'messages',    prevStart, prevEnd, setPrevMessages],
    ];

    setProgress({ current: 0, total: tasks.length, label: 'Starting…' });
    for (let i = 0; i < tasks.length; i++) {
      const [label, type, s, e, setter] = tasks[i];
      setProgress({ current: i, total: tasks.length, label });
      // eslint-disable-next-line no-await-in-loop
      const rows = await fetchOne(label, type, s, e);
      setter(rows);
    }
    setProgress({ current: tasks.length, total: tasks.length, label: 'Done' });
    setLoading(false);
    setHasLoaded(true);
    setFiltersCollapsed(true);
    setTimeout(() => setProgress(null), 1500);
  }, [startDate, endDate, fetchOne]);

  // Sticky scope: the user can have a customer / filter / facility selected
  // globally (set from My Customers, My Filters, or another analysis page).
  // This hook keeps the StickyScopeBar and the in-page selectors in sync,
  // and auto-loads on first apply of each scope. See useScopeSync.js.
  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: loadAll,
    ready: Boolean(startDate && endDate),
  });

  // ---- Filtering helpers -------------------------------------------------
  const facIds = selectedFacilityIds;

  // For referrals/vitalrounds: receiver-only — a row only counts toward
  // the customer's view when the receiving facility is in the selected
  // set. The page's premise is "what activity is INBOUND to this customer"
  // (the value side of every collaboration); outbound rows where one of
  // the customer's facilities is the sender belong to whoever they're
  // sending to, not to us.
  //
  // The Spokes tab is unaffected by this in practice: a spoke is, by
  // definition, NOT in any customer's facility set, so the previous
  // "sender in scope" branch never matched a spoke-sender row anyway.
  // Receiver-only just removes the (nonsensical here) ambiguity.
  const filterReferralRow = useCallback((r) => {
    if (removeTestData && isTestRecord(r)) return false;
    if (removeVitalEngineUsers && isVitalEngineRow(r)) return false;
    if (facIds.size === 0) return true;
    const recv = f(r, 'receiving_facility_id');
    return recv != null && facIds.has(String(recv));
  }, [facIds, removeTestData, removeVitalEngineUsers]);

  // For per-user rollups (logins / messages): match on the user's primary
  // facility_id. Use the shared facility heuristic so the test-data filter
  // catches MyHeart / VitalEngine / VE-Engineering facilities here too —
  // those are staffed by VitalEngine employees, so the data is unlikely to
  // be real customer activity.
  const filterUserRollupRow = useCallback((r) => {
    const facName = f(r, 'facility_name');
    if (removeTestData && isTestFacilityName(facName)) return false;
    if (removeVitalEngineUsers && isVitalEngineRow(r)) return false;
    if (facIds.size === 0) return true;
    const fid = f(r, 'facility_id');
    return fid != null && facIds.has(String(fid));
  }, [facIds, removeTestData, removeVitalEngineUsers]);

  // ---- Filtered & combined record sets ----------------------------------
  const filteredReferrals  = useMemo(() => referrals.filter(filterReferralRow),     [referrals, filterReferralRow]);
  const filteredVrounds    = useMemo(() => vrounds.filter(filterReferralRow),       [vrounds, filterReferralRow]);
  const filteredCollab     = useMemo(() => [...filteredReferrals, ...filteredVrounds], [filteredReferrals, filteredVrounds]);
  const filteredLogins     = useMemo(() => logins.filter(filterUserRollupRow),      [logins, filterUserRollupRow]);
  const filteredMessages   = useMemo(() => messages.filter(filterUserRollupRow),    [messages, filterUserRollupRow]);
  const filteredPrevRef    = useMemo(() => prevReferrals.filter(filterReferralRow), [prevReferrals, filterReferralRow]);
  const filteredPrevLogins = useMemo(() => prevLogins.filter(filterUserRollupRow),  [prevLogins, filterUserRollupRow]);
  const filteredPrevMsgs   = useMemo(() => prevMessages.filter(filterUserRollupRow),[prevMessages, filterUserRollupRow]);

  // ---- Aggregations ------------------------------------------------------
  // Each of these returns [{ label, count, sub? }] sorted desc.

  const topByLogins = useMemo(() => aggregateUserCounts(filteredLogins, LOGIN_FIELDS), [filteredLogins]);
  const topMessageSenders = useMemo(() => aggregateUserCounts(filteredMessages, MESSAGE_SENT_FIELDS), [filteredMessages]);

  // "Sending" / "Referral senders" = the actual sending physician
  // (sending_*). The typist (creating_*) is a separate concept — see
  // REFERRAL-FIELD-NAMING.md.
  const topReferralSenders = useMemo(
    () => topNFromCounter(buildPersonCounter(filteredCollab, 'sending_user_id', 'sending_user_name', 'sending_facility', 'sending_speciality')),
    [filteredCollab]
  );
  const topReferralReceivers = useMemo(
    () => topNFromCounter(buildPersonCounter(filteredCollab, 'receiving_user_id', 'receiving_user_name', 'receiving_facility', 'receiving_speciality')),
    [filteredCollab]
  );

  const topReceivingFacs = useMemo(
    () => topNFromCounter(buildSimpleCounter(filteredCollab, 'receiving_facility')),
    [filteredCollab]
  );
  const topSendingFacs = useMemo(
    () => topNFromCounter(buildSimpleCounter(filteredCollab, 'sending_facility')),
    [filteredCollab]
  );

  const topSendingSpecs = useMemo(
    () => topNFromCounter(buildSimpleCounter(filteredCollab, 'sending_speciality')),
    [filteredCollab]
  );
  const topReceivingSpecs = useMemo(
    () => topNFromCounter(buildSimpleCounter(filteredCollab, 'receiving_speciality')),
    [filteredCollab]
  );

  const topReferralReasons = useMemo(
    () => topNFromCounter(buildSimpleCounter(filteredCollab, 'referral_for')),
    [filteredCollab]
  );

  // Completion rate by receiving facility (min 5 referrals to be ranked).
  const completionByFac = useMemo(() => {
    const totals = new Map();
    const completed = new Map();
    for (const r of filteredCollab) {
      const fac = f(r, 'receiving_facility');
      if (!fac) continue;
      totals.set(fac, (totals.get(fac) || 0) + 1);
      const status = String(f(r, 'referral_status') || '');
      if (COMPLETED_RX.test(status)) completed.set(fac, (completed.get(fac) || 0) + 1);
    }
    const rows = [];
    for (const [fac, total] of totals) {
      if (total < 5) continue;
      const c = completed.get(fac) || 0;
      rows.push({ label: fac, total, completed: c, rate: c / total });
    }
    rows.sort((a, b) => b.rate - a.rate);
    return rows;
  }, [filteredCollab]);

  // Trend cards: combined "engagement score" per user across logins +
  // messages + referrals (both sides), this period vs prior. Then we sort
  // by absolute drop / rise and surface the top 10.
  const trendUsers = useMemo(() => {
    const cur = scoreUsers(filteredLogins, filteredMessages, filteredCollab);
    const prev = scoreUsers(filteredPrevLogins, filteredPrevMsgs, filteredPrevRef);

    const userIds = new Set([...cur.keys(), ...prev.keys()]);
    const rows = [];
    for (const id of userIds) {
      const a = cur.get(id) || zeroUser();
      const b = prev.get(id) || zeroUser();
      const name = a.name || b.name || '(unknown user)';
      const facility = a.facility || b.facility || '';
      const speciality = a.speciality || b.speciality || '';
      rows.push({
        id, name, facility, speciality,
        currentScore: a.score, previousScore: b.score,
        delta: a.score - b.score,
      });
    }
    return rows;
  }, [filteredLogins, filteredMessages, filteredCollab, filteredPrevLogins, filteredPrevMsgs, filteredPrevRef]);

  // Drop-off: was active prior (>=5 actions), now down >=50% AND lost at
  // least 5 actions. Sorted by absolute drop.
  const droppedUsers = useMemo(() => {
    return trendUsers
      .filter(u => u.previousScore >= 5 && u.delta <= -5 && (u.previousScore - u.currentScore) / u.previousScore >= 0.5)
      .sort((a, b) => a.delta - b.delta) // most negative first
      .slice(0, TOP_N);
  }, [trendUsers]);

  // Rising stars: gained at least 5 actions AND grew at least 50% (or
  // came from zero — those are the new adopters CS will love).
  const risingUsers = useMemo(() => {
    return trendUsers
      .filter(u => u.delta >= 5 && (u.previousScore === 0 || u.delta / Math.max(1, u.previousScore) >= 0.5))
      .sort((a, b) => b.delta - a.delta)
      .slice(0, TOP_N);
  }, [trendUsers]);

  // Facility-level referral volume trend, same logic as trendUsers but
  // counted as referrals received per receiving_facility.
  const facilityVolumeTrend = useMemo(() => {
    const cur = facilityCount(filteredReferrals);
    const prev = facilityCount(filteredPrevRef);
    const facs = new Set([...cur.keys(), ...prev.keys()]);
    const rows = [];
    for (const fac of facs) {
      const c = cur.get(fac) || 0;
      const p = prev.get(fac) || 0;
      rows.push({ label: fac, current: c, previous: p, delta: c - p });
    }
    return rows;
  }, [filteredReferrals, filteredPrevRef]);

  const facilitiesDeclining = useMemo(() => {
    return facilityVolumeTrend
      .filter(r => r.previous >= 10 && r.delta <= -3 && (r.previous - r.current) / r.previous >= 0.25)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, TOP_N);
  }, [facilityVolumeTrend]);

  const facilitiesGrowing = useMemo(() => {
    return facilityVolumeTrend
      .filter(r => r.delta >= 3 && (r.previous === 0 || r.delta / Math.max(1, r.previous) >= 0.25))
      .sort((a, b) => b.delta - a.delta)
      .slice(0, TOP_N);
  }, [facilityVolumeTrend]);

  // ---- Spoke (non-customer) aggregations -------------------------------
  //
  // A "spoke" facility is any facility that does NOT appear in the union
  // of every customer's resolved-facility set — i.e. a free, non-customer
  // account. Spoke activity is a leading indicator for sales: a spoke
  // user / facility that's referring or logging in heavily is someone the
  // company should be talking to about converting to a paid account.
  //
  // `allCustomerFacilityIds` may be null while loading; treat that as
  // "don't know yet" and short-circuit the spoke filters so we don't
  // mis-attribute every facility as a spoke during the brief loading
  // window after page mount.

  const spokeReady = allCustomerFacilityIds != null;

  const isSpokeFacilityId = useCallback((id) => {
    if (!spokeReady) return false;
    if (id == null || id === '') return false;
    // Normalize to the same canonical integer-string form customer
    // facility IDs are stored under (CustomerFacilityResolver writes
    // "1706" not "1706.0").
    const n = Math.round(parseFloat(String(id)));
    if (Number.isNaN(n)) return false;
    return !allCustomerFacilityIds.has(String(n));
  }, [spokeReady, allCustomerFacilityIds]);

  // Spoke base streams: test/VE-cleaned but NOT scoped to the customer's
  // facility set. Spoke users by definition log in at facilities OUTSIDE
  // the customer's hierarchy, so passing logins/messages through
  // `filterUserRollupRow` (which requires facility_id ∈ facIds) would
  // strip 100% of spoke rows whenever a customer scope is active. We
  // build these from the raw arrays and apply only the test/VE filters
  // here; spoke + cohort filtering happens below.
  const filterSpokeUserRollupRow = useCallback((r) => {
    const facName = f(r, 'facility_name');
    if (removeTestData && isTestFacilityName(facName)) return false;
    if (removeVitalEngineUsers && isVitalEngineRow(r)) return false;
    return true;
  }, [removeTestData, removeVitalEngineUsers]);

  const spokeBaseLogins   = useMemo(() => logins.filter(filterSpokeUserRollupRow),       [logins, filterSpokeUserRollupRow]);
  const spokeBaseMessages = useMemo(() => messages.filter(filterSpokeUserRollupRow),     [messages, filterSpokeUserRollupRow]);
  const spokeBasePrevLogins = useMemo(() => prevLogins.filter(filterSpokeUserRollupRow), [prevLogins, filterSpokeUserRollupRow]);
  const spokeBasePrevMsgs   = useMemo(() => prevMessages.filter(filterSpokeUserRollupRow), [prevMessages, filterSpokeUserRollupRow]);

  // Referrals where the SENDING facility is a spoke (the actual referring
  // physician's facility — patient source). Distinct from creating, which
  // is the typist who keyed the referral in.
  const spokeSendingRefs = useMemo(
    () => spokeReady ? filteredCollab.filter(r => isSpokeFacilityId(f(r, 'sending_facility_id'))) : [],
    [filteredCollab, spokeReady, isSpokeFacilityId]
  );
  // Referrals where the CREATING facility is a spoke (the typist's
  // location — useful for spotting non-customer support staff).
  const spokeCreatingRefs = useMemo(
    () => spokeReady ? filteredCollab.filter(r => isSpokeFacilityId(f(r, 'creating_facility_id'))) : [],
    [filteredCollab, spokeReady, isSpokeFacilityId]
  );
  const spokePrevRefs = useMemo(
    () => spokeReady ? filteredPrevRef.filter(r => isSpokeFacilityId(f(r, 'sending_facility_id'))) : [],
    [filteredPrevRef, spokeReady, isSpokeFacilityId]
  );

  // Cohort restriction: when a customer scope is active, only count spoke
  // logins/messages from users who have actually referred into THIS
  // customer (current or prior window). Otherwise a CS rep on customer X
  // would see global spoke chatter that has nothing to do with their
  // territory. With no scope active we use the full spoke universe.
  const scopedSpokeUserIds = useMemo(() => {
    if (facIds.size === 0) return null;
    const ids = new Set();
    const collect = (rows, key) => {
      for (const r of rows) {
        const id = f(r, key);
        if (id != null && id !== '') ids.add(String(id));
      }
    };
    collect(spokeSendingRefs,  'sending_user_id');
    collect(spokePrevRefs,     'sending_user_id');
    collect(spokeCreatingRefs, 'creating_user_id');
    return ids;
  }, [facIds, spokeSendingRefs, spokePrevRefs, spokeCreatingRefs]);

  const isInSpokeCohort = useCallback((r) => {
    if (scopedSpokeUserIds == null) return true;
    const uid = f(r, 'user_id');
    return uid != null && scopedSpokeUserIds.has(String(uid));
  }, [scopedSpokeUserIds]);

  // Logins / messages at spoke facilities, narrowed to the spoke cohort
  // when a customer scope is active.
  const spokeLogins = useMemo(
    () => spokeReady
      ? spokeBaseLogins.filter(r => isSpokeFacilityId(f(r, 'facility_id')) && isInSpokeCohort(r))
      : [],
    [spokeBaseLogins, spokeReady, isSpokeFacilityId, isInSpokeCohort]
  );
  const spokeMessages = useMemo(
    () => spokeReady
      ? spokeBaseMessages.filter(r => isSpokeFacilityId(f(r, 'facility_id')) && isInSpokeCohort(r))
      : [],
    [spokeBaseMessages, spokeReady, isSpokeFacilityId, isInSpokeCohort]
  );
  const spokePrevLogins = useMemo(
    () => spokeReady
      ? spokeBasePrevLogins.filter(r => isSpokeFacilityId(f(r, 'facility_id')) && isInSpokeCohort(r))
      : [],
    [spokeBasePrevLogins, spokeReady, isSpokeFacilityId, isInSpokeCohort]
  );
  const spokePrevMsgs = useMemo(
    () => spokeReady
      ? spokeBasePrevMsgs.filter(r => isSpokeFacilityId(f(r, 'facility_id')) && isInSpokeCohort(r))
      : [],
    [spokeBasePrevMsgs, spokeReady, isSpokeFacilityId, isInSpokeCohort]
  );

  // ----- Spoke USER leaderboards -------------------------------------------
  const topSpokeUsersByLogins = useMemo(
    () => aggregateUserCounts(spokeLogins, LOGIN_FIELDS),
    [spokeLogins]
  );
  const topSpokeMessageSenders = useMemo(
    () => aggregateUserCounts(spokeMessages, MESSAGE_SENT_FIELDS),
    [spokeMessages]
  );
  const topSpokeReferralSenders = useMemo(
    () => topNFromCounter(buildPersonCounter(
      spokeSendingRefs, 'sending_user_id', 'sending_user_name', 'sending_facility', 'sending_speciality')),
    [spokeSendingRefs]
  );
  const topSpokeReferralCreators = useMemo(
    () => topNFromCounter(buildPersonCounter(
      spokeCreatingRefs, 'creating_user_id', 'creating_user_name', 'creating_facility', 'creating_speciality')),
    [spokeCreatingRefs]
  );

  // ----- Spoke FACILITY leaderboards ---------------------------------------
  // Logins/messages: bucket by `facility_name` for human-readable labels.
  // Falls back to facility_id when name is missing.
  const topSpokeFacilitiesByLogins = useMemo(() => {
    const m = new Map();
    for (const r of spokeLogins) {
      const name = f(r, 'facility_name') || `Facility ${f(r, 'facility_id') ?? '?'}`;
      const total = sumFields(r, LOGIN_FIELDS);
      if (total <= 0) continue;
      m.set(name, (m.get(name) || 0) + total);
    }
    return topNFromCounter(m);
  }, [spokeLogins]);

  const topSpokeFacilitiesBySending = useMemo(
    () => topNFromCounter(buildSimpleCounter(spokeSendingRefs, 'sending_facility')),
    [spokeSendingRefs]
  );
  const topSpokeFacilitiesByCreating = useMemo(
    () => topNFromCounter(buildSimpleCounter(spokeCreatingRefs, 'creating_facility')),
    [spokeCreatingRefs]
  );

  // ----- Spoke trends (drop-off + rising) ----------------------------------
  // Reuses the same scoreUsers helper but operates on the spoke-only
  // (and cohort-narrowed) streams defined above.
  const spokeTrendUsers = useMemo(() => {
    const cur = scoreUsers(spokeLogins, spokeMessages, spokeSendingRefs);
    const prev = scoreUsers(spokePrevLogins, spokePrevMsgs, spokePrevRefs);
    const ids = new Set([...cur.keys(), ...prev.keys()]);
    const rows = [];
    for (const id of ids) {
      const a = cur.get(id) || zeroUser();
      const b = prev.get(id) || zeroUser();
      const name = a.name || b.name || '(unknown user)';
      const facility = a.facility || b.facility || '';
      const speciality = a.speciality || b.speciality || '';
      rows.push({
        id, name, facility, speciality,
        currentScore: a.score, previousScore: b.score,
        delta: a.score - b.score,
      });
    }
    return rows;
  }, [spokeLogins, spokeMessages, spokeSendingRefs, spokePrevLogins, spokePrevMsgs, spokePrevRefs]);

  const spokeUsersDropping = useMemo(
    () => spokeTrendUsers
      .filter(u => u.previousScore >= 5 && u.delta <= -5 && (u.previousScore - u.currentScore) / u.previousScore >= 0.5)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, TOP_N),
    [spokeTrendUsers]
  );
  const spokeUsersRising = useMemo(
    () => spokeTrendUsers
      .filter(u => u.delta >= 5 && (u.previousScore === 0 || u.delta / Math.max(1, u.previousScore) >= 0.5))
      .sort((a, b) => b.delta - a.delta)
      .slice(0, TOP_N),
    [spokeTrendUsers]
  );

  // Spoke FACILITY volume trend (sending side — referrals originating
  // from a spoke facility this window vs. prior).
  const spokeFacilityVolumeTrend = useMemo(() => {
    const count = (rows) => {
      const m = new Map();
      for (const r of rows) {
        const fac = f(r, 'sending_facility');
        if (!fac) continue;
        m.set(fac, (m.get(fac) || 0) + 1);
      }
      return m;
    };
    const cur = count(spokeSendingRefs);
    const prev = count(spokePrevRefs);
    const facs = new Set([...cur.keys(), ...prev.keys()]);
    const rows = [];
    for (const fac of facs) {
      const c = cur.get(fac) || 0;
      const p = prev.get(fac) || 0;
      rows.push({ label: fac, current: c, previous: p, delta: c - p });
    }
    return rows;
  }, [spokeSendingRefs, spokePrevRefs]);

  const spokeFacilitiesDeclining = useMemo(
    () => spokeFacilityVolumeTrend
      .filter(r => r.previous >= 10 && r.delta <= -3 && (r.previous - r.current) / r.previous >= 0.25)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, TOP_N),
    [spokeFacilityVolumeTrend]
  );
  const spokeFacilitiesGrowing = useMemo(
    () => spokeFacilityVolumeTrend
      .filter(r => r.delta >= 3 && (r.previous === 0 || r.delta / Math.max(1, r.previous) >= 0.25))
      .sort((a, b) => b.delta - a.delta)
      .slice(0, TOP_N),
    [spokeFacilityVolumeTrend]
  );

  // Quick-glance numbers for the Spokes tab header so users know the
  // denominator before scanning leaderboards.
  const spokeStats = useMemo(() => ({
    sendingRefs:  spokeSendingRefs.length,
    creatingRefs: spokeCreatingRefs.length,
    loginRows:    spokeLogins.length,
    messageRows:  spokeMessages.length,
  }), [spokeSendingRefs, spokeCreatingRefs, spokeLogins, spokeMessages]);

  // Facility ids known to data, for the FacilityFilter modal.
  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    [...referrals, ...vrounds].forEach(r => {
      const recv = f(r, 'receiving_facility_id');
      const send = f(r, 'sending_facility_id');
      if (recv != null) ids.add(Math.round(parseFloat(String(recv))).toString());
      if (send != null) ids.add(Math.round(parseFloat(String(send))).toString());
    });
    [...logins, ...messages].forEach(r => {
      const fid = f(r, 'facility_id');
      if (fid != null) ids.add(Math.round(parseFloat(String(fid))).toString());
    });
    return ids;
  }, [referrals, vrounds, logins, messages]);

  const totalCollabRows = filteredCollab.length;

  // User IDs shown on People + Spokes tabs — batch-fetched for contact hover cards.
  const personUserIds = useMemo(() => {
    const ids = new Set();
    const addItems = (items) => {
      for (const it of items || []) {
        const id = Number(it.userId);
        if (id > 0) ids.add(id);
      }
    };
    const addTrend = (rows) => {
      for (const u of rows || []) {
        const id = Number(u.id);
        if (id > 0) ids.add(id);
      }
    };
    addItems(topByLogins);
    addItems(topMessageSenders);
    addItems(topReferralSenders);
    addItems(topReferralReceivers);
    addTrend(droppedUsers);
    addTrend(risingUsers);
    addItems(topSpokeUsersByLogins);
    addItems(topSpokeMessageSenders);
    addItems(topSpokeReferralSenders);
    addItems(topSpokeReferralCreators);
    addTrend(spokeUsersDropping);
    addTrend(spokeUsersRising);
    return [...ids];
  }, [
    topByLogins, topMessageSenders, topReferralSenders, topReferralReceivers,
    droppedUsers, risingUsers,
    topSpokeUsersByLogins, topSpokeMessageSenders, topSpokeReferralSenders,
    topSpokeReferralCreators, spokeUsersDropping, spokeUsersRising,
  ]);

  // ---- AI-driven Suggested Actions (top-10 priority list) --------------
  //
  // After Load Insights finishes, fire ONE batched POST to
  // /api/vera/insight-actions sending every visible trend row across all
  // eight tables. The AI's job is to TRIAGE that data and return the
  // top 10 most important actions a CS rep should take this week —
  // ranked, with a short "why" for each. A CS rep can't realistically
  // do 60+ actions, so dumping one-per-row was both unhelpful and
  // expensive (and was timing out at the nginx 60s ceiling).
  //
  // We use a monotonic request token (`actionsReqRef`) so that if the
  // user changes the date range / scope and reloads while a previous
  // AI call is still in flight, the stale response is dropped.
  //
  // Server response shape: { actions: [{ rank, category, subject,
  // metric, action, why, groupKey }] }.
  const [insightActions, setInsightActions] = useState(null); // null = not loaded; [] = loaded but empty
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState(null);
  const [actionsMeta, setActionsMeta] = useState(null);      // { provider, model, generatedAt, parseWarning }
  const actionsReqRef = useRef(0);

  // Build the payload from the current trend tables. Stable serialization
  // (sort by current/delta etc.) means re-renders that don't actually
  // change the underlying rows won't re-fire the AI call.
  const actionsPayload = useMemo(() => {
    if (!hasLoaded) return null;

    const userRow = (u) => ({
      key:        String(u.id),
      subject:    u.name,
      facility:   u.facility,
      speciality: u.speciality,
      current:    u.currentScore,
      previous:   u.previousScore,
      delta:      u.delta,
    });
    const facRow = (r) => ({
      key:        r.label,
      subject:    r.label,
      current:    r.current,
      previous:   r.previous,
      delta:      r.delta,
    });

    const groups = [
      {
        key: 'users-dropping',
        label: 'Customer-side users with biggest drop-off',
        intent: 're-engagement: prevent churn',
        rows: droppedUsers.map(userRow),
      },
      {
        key: 'users-rising',
        label: 'Customer-side users with biggest engagement gains',
        intent: 'celebrate, expand usage, source case-study material',
        rows: risingUsers.map(userRow),
      },
      {
        key: 'facilities-declining',
        label: 'Customer facilities with biggest receiving-volume drop',
        intent: 'relationship check-in: investigate why volume slipped',
        rows: facilitiesDeclining.map(facRow),
      },
      {
        key: 'facilities-growing',
        label: 'Customer facilities with biggest receiving-volume growth',
        intent: 'celebrate, ask for referral, replicate the pattern elsewhere',
        rows: facilitiesGrowing.map(facRow),
      },
      {
        key: 'spoke-users-dropping',
        label: 'Spoke (free, non-customer) users dropping off',
        intent: 'last-chance conversion outreach before they go dark',
        rows: spokeUsersDropping.map(userRow),
      },
      {
        key: 'spoke-users-rising',
        label: 'Spoke (free, non-customer) users heating up',
        intent: 'top sales-conversion leads — engage now',
        rows: spokeUsersRising.map(userRow),
      },
      {
        key: 'spoke-facilities-declining',
        label: 'Spoke facilities with declining sending volume',
        intent: 'sales check-in: was building a referral pattern but cooling',
        rows: spokeFacilitiesDeclining.map(facRow),
      },
      {
        key: 'spoke-facilities-growing',
        label: 'Spoke facilities with growing sending volume',
        intent: 'sales engagement: spoke is building usage on its own — convert',
        rows: spokeFacilitiesGrowing.map(facRow),
      },
    ].filter(g => g.rows.length > 0);

    if (groups.length === 0) return null;

    // Build a scope label for the prompt.
    let scopeLabel = 'All facilities';
    if (activeCustomerId) {
      const c = customers.find(x => String(x.id) === String(activeCustomerId));
      scopeLabel = c?.name ? `Customer: ${c.name}` : `Customer #${activeCustomerId}`;
    } else if (activeFilterId) {
      const sf = savedFilters.find(s => String(s.id) === String(activeFilterId));
      scopeLabel = sf?.name ? `Saved filter: ${sf.name}` : `Filter #${activeFilterId}`;
    } else if (selectedFacilityIds.size > 0) {
      scopeLabel = `Custom set of ${selectedFacilityIds.size} facilities`;
    }

    const { prevStart, prevEnd } = previousWindow(startDate, endDate);

    return {
      startDate, endDate,
      priorStartDate: prevStart, priorEndDate: prevEnd,
      scopeLabel,
      groups,
    };
  }, [
    hasLoaded,
    droppedUsers, risingUsers,
    facilitiesDeclining, facilitiesGrowing,
    spokeUsersDropping, spokeUsersRising,
    spokeFacilitiesDeclining, spokeFacilitiesGrowing,
    activeCustomerId, activeFilterId, selectedFacilityIds,
    customers, savedFilters,
    startDate, endDate,
  ]);

  useEffect(() => {
    if (!actionsPayload) return;

    const reqId = ++actionsReqRef.current;
    setActionsLoading(true);
    setActionsError(null);

    (async () => {
      try {
        const res = await client.post('/api/vera/insight-actions', actionsPayload);
        // Drop stale responses — user may have re-loaded with a new
        // window before we got back.
        if (reqId !== actionsReqRef.current) return;

        // New schema: server returns a ranked array. Sort by rank in
        // case the model omitted ranks or ranked out of order.
        const list = Array.isArray(res.data?.actions) ? [...res.data.actions] : [];
        list.sort((a, b) => (Number(a?.rank) || 99) - (Number(b?.rank) || 99));
        setInsightActions(list);
        setActionsMeta({
          provider: res.data?.provider,
          model: res.data?.model,
          generatedAt: res.data?.generatedAt,
          // parseWarning is a soft message from the backend when the AI
          // returned partial / oddly-shaped JSON — surface it inline on
          // the Actions tab rather than treating as a hard error.
          parseWarning: res.data?.parseWarning || null,
        });
      } catch (err) {
        if (reqId !== actionsReqRef.current) return;
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message;
        let friendly;
        if (status === 503) {
          friendly = 'AI provider not configured. Add ANTHROPIC_API_KEY or OPENAI_API_KEY to .env to enable suggested actions.';
        } else if (status === 504 || /timeout/i.test(msg)) {
          friendly = "The AI took too long to respond. Try again — it's usually faster on the second attempt.";
        } else {
          friendly = `Couldn't fetch suggested actions: ${msg}`;
        }
        setActionsError(friendly);
        setInsightActions([]); // empty list so the tab renders the "no actions" state
      } finally {
        if (reqId === actionsReqRef.current) setActionsLoading(false);
      }
    })();
  }, [actionsPayload]);

  return (
    <>
      <div className="page-header">
        <h1>
          Insights
          <span className="page-header-scope">
            <span className="page-header-sep">&mdash;</span>
            <span className="page-header-name">{currentScope.name}</span>
          </span>
        </h1>
        <p className="page-header-sub">
          A CS rep starter pack: who&apos;s driving usage, who&apos;s slipping, and where the bright spots are.
        </p>
      </div>

      {filtersCollapsed && hasLoaded ? (
        <div className="filter-strip-bar">
          <div className="filter-strip-summary">
            <div className="filter-strip-item">
              <span className="filter-strip-label">Date range</span>
              <span
                className="filter-strip-value"
                title="Month-named presets use full months only - the in-progress month is excluded."
              >
                {dateRangeLabel}
              </span>
            </div>
            <div className="filter-strip-item">
              <span className="filter-strip-label">Test data</span>
              <span className="filter-strip-value">{removeTestData ? 'Hidden' : 'Shown'}</span>
            </div>
            <div className="filter-strip-item">
              <span className="filter-strip-label">VE staff</span>
              <span
                className="filter-strip-value"
                title="When 'Hidden', users at VitalEngine / VE-Engineering / MyHeart facilities are excluded from leaderboards and trends."
              >
                {removeVitalEngineUsers ? 'Hidden' : 'Shown'}
              </span>
            </div>
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-ghost"
              onClick={() => setFiltersCollapsed(false)}
            >
              Edit filters
            </button>
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={loadAll}
              disabled={loading || !startDate || !endDate}
            >
              {loading ? 'Loading...' : 'Reload'}
            </button>
          </div>
        </div>
      ) : (
      <div className="controls-panel">
        <div className="date-range-selector">
          <label htmlFor="insightsDatePreset">Date Range:</label>
          <select
            id="insightsDatePreset"
            value={dateRangePreset}
            onChange={(e) => setDateRangePreset(e.target.value)}
            disabled={loading}
            className="date-range-dropdown"
            title="Month-named presets use FULL months only — the in-progress month is excluded. Use Custom Range for fractional / arbitrary dates."
          >
            <option value="last_month">Last Month (full month)</option>
            <option value="last_3_months">Last 3 Months (full months)</option>
            <option value="last_6_months">Last 6 Months (full months)</option>
            <option value="last_year">Last Year (12 full months)</option>
            <option value="last_18_months">Last 18 Months (full months)</option>
            <option value="last_2_years">Last 2 Years (24 full months)</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {dateRangePreset !== 'custom' && startDate && endDate && (
          <div className="insights-date-hint" title="Trend / drop-off cards then compare this window to an equally-sized window ending the day before it starts.">
            Window: <strong>{startDate}</strong> → <strong>{endDate}</strong>
            {' '}<span className="insights-date-hint-sub">(in-progress month excluded)</span>
          </div>
        )}

        {dateRangePreset === 'custom' && (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            disabled={loading}
          />
        )}

        {savedFilters.length > 0 && (
          <div className="saved-filter-selector">
            <label htmlFor="insightsSavedFilter">Saved Filter:</label>
            <select
              id="insightsSavedFilter"
              value={activeFilterId}
              onChange={(e) => applySavedFilter(e.target.value)}
              className="date-range-dropdown"
            >
              <option value="">None (show all)</option>
              {savedFilters.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.facilityIds?.length || 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="saved-filter-selector">
            <label htmlFor="insightsCustomerFilter">Customer:</label>
            <select
              id="insightsCustomerFilter"
              value={activeCustomerId}
              onChange={(e) => applyCustomerFilter(e.target.value)}
              className="date-range-dropdown"
            >
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c._depth > 0 ? '— ' : ''}{c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button className="filter-button" onClick={() => setShowFacilityFilter(true)}>
          Custom Filter
          {selectedFacilityIds.size > 0 && ` (${selectedFacilityIds.size})`}
        </button>

        <label
          className="collab-test-toggle"
          title="Hide records flagged as test data, with patient names like TEST/TESTLEY/John Doe, or sent to/from VitalEngine or MyHeart facilities."
        >
          <input
            type="checkbox"
            checked={removeTestData}
            onChange={(e) => setRemoveTestData(e.target.checked)}
          />
          Remove Test Data
        </label>

        <label
          className="collab-test-toggle"
          title="Hide VitalEngine staff (users at facilities or specialties matching VitalEngine / VE-Engineering / MyHeart) from leaderboards and trend cards. Independent of Remove Test Data."
        >
          <input
            type="checkbox"
            checked={removeVitalEngineUsers}
            onChange={(e) => setRemoveVitalEngineUsers(e.target.checked)}
          />
          Remove VitalEngine Users
        </label>

        <button
          className="load-button"
          onClick={loadAll}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Loading...' : 'Load Insights'}
        </button>

        {progress && (
          <div className="data-info">
            {progress.label} ({progress.current}/{progress.total})
          </div>
        )}

        {errors.length > 0 && (
          <div className="error-message">
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        {hasLoaded && (
          <div className="data-info">
            <strong>{totalCollabRows.toLocaleString()}</strong> referral/vitalround records ·
            {' '}<strong>{filteredLogins.length.toLocaleString()}</strong> login rollups ·
            {' '}<strong>{filteredMessages.length.toLocaleString()}</strong> message rollups
          </div>
        )}
      </div>
      )}

      {filtersCollapsed && progress && (
        <div className="data-info" style={{ marginBottom: '0.75rem' }}>
          {progress.label} ({progress.current}/{progress.total})
        </div>
      )}

      {filtersCollapsed && errors.length > 0 && (
        <div className="error-message" style={{ marginBottom: '0.75rem' }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {filtersCollapsed && hasLoaded && (
        <div className="data-info" style={{ marginBottom: '1rem' }}>
          <strong>{totalCollabRows.toLocaleString()}</strong> referral/vitalround records &middot;
          {' '}<strong>{filteredLogins.length.toLocaleString()}</strong> login rollups &middot;
          {' '}<strong>{filteredMessages.length.toLocaleString()}</strong> message rollups
        </div>
      )}

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && facilityHierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={facilityHierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {!hasLoaded ? (
        <div className="chart-panel">
          <div className="empty-state">
            <p>No insights loaded yet.</p>
            <p>
              Pick a window above and press <strong>Load Insights</strong>. The page pulls referrals, VitalRounds,
              logins, and messages for the current and prior windows so it can show trends and drop-off signals.
              An administrator can populate the local cache from <strong>Admin → Data Sources</strong>.
            </p>
          </div>
        </div>
      ) : (
        <PersonContactsProvider userIds={personUserIds}>
        <>
          <nav className="insights-tabs" role="tablist" aria-label="Insights categories">
            {TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={`insights-tab ${activeTab === t.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                title={t.hint}
              >
                {t.label}
                {t.id === 'actions' && <span className="insights-tab-badge">AI</span>}
              </button>
            ))}
          </nav>

          {activeTab === 'people' && (
            <div className="insights-grid">
              <InsightCard
                title="Most Active Users (Logins)"
                subtitle="Total logins across all clients in the selected window."
                items={topByLogins}
                color="blue"
                emptyHint="Logins haven't been pulled into the cache yet."
              />
              <InsightCard
                title="Top Message Senders"
                subtitle="Sum of messages sent across referral, vmail, vchat, vrounds, and vtransfer."
                items={topMessageSenders}
                color="teal"
                emptyHint="Messages haven't been pulled into the cache yet."
              />
              <InsightCard
                title="Top Referral Senders"
                subtitle="Providers initiating the most referrals (Referrals + VitalRounds)."
                items={topReferralSenders}
                color="green"
                emptyHint="No referrals match the current filter."
              />
              <InsightCard
                title="Top Referral Receivers"
                subtitle="Providers being referred to most often."
                items={topReferralReceivers}
                color="green"
                emptyHint="No referrals match the current filter."
              />
              <TrendCard
                title="Users Slipping (Drop-off)"
                subtitle="Was meaningfully active in the prior window, has dropped 50%+ this window. Prime CS outreach list."
                tone="bad"
                rows={droppedUsers}
                emptyHint="Nobody fits the drop-off pattern in this window — good news!"
                info={DROP_OFF_INFO}
              />
              <TrendCard
                title="Rising Stars"
                subtitle="Big jumps in usage vs. the prior window — including new adopters who weren't active before."
                tone="good"
                rows={risingUsers}
                emptyHint="No standout climbers this window."
                info={RISING_INFO}
              />
            </div>
          )}

          {activeTab === 'facilities' && (
            <div className="insights-grid">
              <InsightCard
                title="Top Receiving Facilities"
                subtitle="Where referrals are being sent."
                items={topReceivingFacs}
                color="teal"
                emptyHint="No referrals match the current filter."
              />
              <InsightCard
                title="Top Sending Facilities"
                subtitle="Where referrals originate."
                items={topSendingFacs}
                color="blue"
                emptyHint="No referrals match the current filter."
              />
              <FacilityTrendCard
                title="Facilities Losing Volume"
                subtitle="Receiving fewer referrals this window than prior. Worth checking on the relationship."
                tone="bad"
                rows={facilitiesDeclining}
                info={FACILITY_DECLINE_INFO}
              />
              <FacilityTrendCard
                title="Facilities Growing Volume"
                subtitle="Receiving more referrals this window than prior."
                tone="good"
                rows={facilitiesGrowing}
                info={FACILITY_GROW_INFO}
              />
              <CompletionCard rows={completionByFac} />
            </div>
          )}

          {activeTab === 'specialties' && (
            <div className="insights-grid">
              <InsightCard
                title="Top Sending Specialties"
                subtitle="Specialty of the referring physician (patient source)."
                items={topSendingSpecs}
                color="blue"
                emptyHint="No referrals match the current filter."
              />
              <InsightCard
                title="Top Receiving Specialties"
                subtitle="Specialty of the receiving physician."
                items={topReceivingSpecs}
                color="teal"
                emptyHint="No referrals match the current filter."
              />
              <InsightCard
                title="Top Referral Reasons"
                subtitle="Free-text reasons captured in 'referral_for'."
                items={topReferralReasons}
                color="green"
                emptyHint="No referrals match the current filter."
              />
            </div>
          )}

          {activeTab === 'spokes' && (
            <SpokesTabBody
              spokeReady={spokeReady}
              spokeStats={spokeStats}
              hasCustomerScope={facIds.size > 0}
              topSpokeUsersByLogins={topSpokeUsersByLogins}
              topSpokeMessageSenders={topSpokeMessageSenders}
              topSpokeReferralSenders={topSpokeReferralSenders}
              topSpokeReferralCreators={topSpokeReferralCreators}
              topSpokeFacilitiesByLogins={topSpokeFacilitiesByLogins}
              topSpokeFacilitiesBySending={topSpokeFacilitiesBySending}
              topSpokeFacilitiesByCreating={topSpokeFacilitiesByCreating}
              spokeUsersDropping={spokeUsersDropping}
              spokeUsersRising={spokeUsersRising}
              spokeFacilitiesDeclining={spokeFacilitiesDeclining}
              spokeFacilitiesGrowing={spokeFacilitiesGrowing}
            />
          )}

          {activeTab === 'actions' && (
            <ActionsTabBody
              actionsLoading={actionsLoading}
              actionsError={actionsError}
              actionsMeta={actionsMeta}
              actions={insightActions}
            />
          )}
        </>
        </PersonContactsProvider>
      )}

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={facilityHierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => { setSelectedFacilityIds(ids); setActiveFilterId(''); setActiveCustomerId(''); }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </>
  );
}

// =========================================================================
// Aggregation helpers
// =========================================================================

// User-rollup feeds (logins / messages) come pre-aggregated by user_id+day.
// We sum the listed counter fields over all rows per user_id and return a
// top-N list with the user's name + primary facility for context.
function aggregateUserCounts(rows, fields) {
  const m = new Map();
  for (const r of rows) {
    const id = pickInt(r, 'user_id');
    const total = sumFields(r, fields);
    if (total <= 0) continue;
    const agg = m.get(id) || {
      name: f(r, 'user_display_name') || '(unknown user)',
      facility: f(r, 'facility_name') || '',
      speciality: f(r, 'speciality') || '',
      total: 0,
    };
    agg.total += total;
    m.set(id, agg);
  }
  return Array.from(m.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, TOP_N)
    .map(([id, a]) => ({
      label: a.name,
      userId: Number(id),
      sub: [a.speciality, a.facility].filter(Boolean).join(' · '),
      count: a.total,
    }));
}

// Person-counter for referrals: keyed on user_id, displays user_name and
// the user's own facility/speciality (the side we're counting).
function buildPersonCounter(rows, idKey, nameKey, facKey, specKey) {
  const m = new Map();
  for (const r of rows) {
    const id = f(r, idKey);
    if (id == null) continue;
    const key = String(id);
    const agg = m.get(key) || {
      userId: Number(id) || Number(key),
      name: f(r, nameKey) || '(unknown)',
      facility: f(r, facKey) || '',
      speciality: f(r, specKey) || '',
      count: 0,
    };
    agg.count += 1;
    m.set(key, agg);
  }
  return m;
}

function buildSimpleCounter(rows, key) {
  const m = new Map();
  for (const r of rows) {
    const v = f(r, key);
    if (!v) continue;
    m.set(v, (m.get(v) || 0) + 1);
  }
  return m;
}

function topNFromCounter(counter, n = TOP_N) {
  if (counter instanceof Map) {
    // Plain key → number map.
    if (counter.size === 0) return [];
    const first = counter.values().next().value;
    if (typeof first === 'number') {
      return Array.from(counter.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([label, count]) => ({ label, count }));
    }
    // Person-counter map — values are { name, facility, speciality, count }
    return Array.from(counter.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, n)
      .map(a => ({
        label: a.name,
        userId: a.userId,
        sub: [a.speciality, a.facility].filter(Boolean).join(' · '),
        count: a.count,
      }));
  }
  return [];
}

// Small inline ⓘ glyph that shows a longer explanation on hover. Uses the
// browser-native title attribute so we don't need a tooltip library and so
// the text is also readable for screen readers / keyboard users.
function InfoIcon({ text }) {
  return (
    <span
      className="insights-info-icon"
      tabIndex={0}
      role="img"
      aria-label={text}
      title={text}
    >
      ⓘ
    </span>
  );
}

// Tooltip strings used by the trend cards. Centralized here so the formula
// description stays in lock-step if the thresholds in droppedUsers /
// risingUsers / facilitiesDeclining / facilitiesGrowing change.
const DROP_OFF_INFO =
  'How this is calculated:\n' +
  '• Engagement score per user = sum of all login types + messages sent + ' +
  'referrals (sent OR received).\n' +
  '• "Prior" = a window the same length as your selected range, ending the ' +
  'day before it starts. Pick "Last 90 days" → prior is the 90 days before that.\n' +
  '• Drop-off filter: had ≥5 actions prior, lost ≥50% AND ≥5 actions this window.\n' +
  '• Top 10 sorted by absolute drop.\n\n' +
  'Note: the 5-action minimum is FIXED, not scaled to window length. Longer ' +
  'date ranges (e.g. "Last year") make 5 actions a very low bar, so more ' +
  'users qualify. Shorter ranges produce a tighter, more meaningful list.';

const RISING_INFO =
  'How this is calculated:\n' +
  '• Same engagement score as Drop-off (logins + messages sent + referrals).\n' +
  '• "Prior" = same-length window ending the day before yours starts.\n' +
  '• Rising filter: gained ≥5 actions AND grew ≥50% (or came from zero — those ' +
  'are brand new adopters).\n' +
  '• Top 10 sorted by absolute gain.\n\n' +
  'Note: thresholds are fixed (not scaled to window length), so a longer date ' +
  'range will produce a busier list.';

const FACILITY_DECLINE_INFO =
  'How this is calculated:\n' +
  '• Counts referrals received per facility this window vs. the same-length ' +
  'prior window.\n' +
  '• Decline filter: had ≥10 referrals prior, lost ≥25% AND ≥3 referrals.\n' +
  '• Top 10 sorted by absolute drop.\n\n' +
  'Note: the 10-referral minimum is fixed — longer date ranges make that easier ' +
  'to clear, so expect more facilities to qualify with wider windows.';

const FACILITY_GROW_INFO =
  'How this is calculated:\n' +
  '• Counts referrals received per facility this window vs. the same-length ' +
  'prior window.\n' +
  '• Growth filter: gained ≥3 referrals AND grew ≥25% (or came from zero).\n' +
  '• Top 10 sorted by absolute gain.\n\n' +
  'Note: thresholds are fixed (not scaled to window length).';

const SPOKE_TAB_INFO =
  'A "spoke" is any facility that is NOT part of any customer (paid) deal — ' +
  'i.e. a free, non-customer VitalEngine account. Spoke activity is a ' +
  'leading indicator: a spoke that\'s sending lots of referrals or logging ' +
  'in heavily is a sales lead.\n\n' +
  'How spokes are detected:\n' +
  '• On page load, we union every customer\'s resolved facility IDs.\n' +
  '• Any cached record whose facility_id (logins/messages) or ' +
  'sending_facility_id / creating_facility_id (referrals) is NOT in that ' +
  'union counts as spoke.\n' +
  '• "Sending" = the actual referring physician\'s facility (patient source).\n' +
  '• "Creating" = the typist who keyed the referral in. They\'re often the ' +
  'same facility but not always — see REFERRAL-FIELD-NAMING.md.';

const SPOKE_DROP_INFO =
  'Same engagement-score formula as the People tab\'s drop-off card, but ' +
  'restricted to users at spoke (non-customer) facilities. These are the ' +
  'free-account users who were ramping up but have gone quiet — prime ' +
  'sales-outreach candidates.\n\n' +
  '• Engagement score = logins + messages sent + referrals (sent or received).\n' +
  '• Drop-off filter: had ≥5 actions prior, lost ≥50% AND ≥5 actions this window.';

const SPOKE_RISE_INFO =
  'Spoke users with the biggest jump in engagement vs. the prior window. ' +
  'These are non-customer accounts gaining traction — the highest-value ' +
  'sales conversations to start.\n\n' +
  '• Same engagement-score formula as drop-off.\n' +
  '• Rising filter: gained ≥5 actions AND grew ≥50% (or came from zero).';

// Score per user across all three feeds, used by Drop-off / Rising cards.
function scoreUsers(loginRows, messageRows, referralRows) {
  const m = new Map();
  const ensure = (id, sample) => {
    const key = String(id);
    if (!m.has(key)) {
      m.set(key, {
        name: sample.name || '',
        facility: sample.facility || '',
        speciality: sample.speciality || '',
        score: 0,
      });
    }
    return m.get(key);
  };

  for (const r of loginRows) {
    const id = pickInt(r, 'user_id');
    if (!id) continue;
    const agg = ensure(id, {
      name: f(r, 'user_display_name'),
      facility: f(r, 'facility_name'),
      speciality: f(r, 'speciality'),
    });
    agg.score += sumFields(r, LOGIN_FIELDS);
  }
  for (const r of messageRows) {
    const id = pickInt(r, 'user_id');
    if (!id) continue;
    const agg = ensure(id, {
      name: f(r, 'user_display_name'),
      facility: f(r, 'facility_name'),
      speciality: f(r, 'speciality'),
    });
    agg.score += sumFields(r, MESSAGE_SENT_FIELDS);
  }
  for (const r of referralRows) {
    // Sender side uses sending_* (canonical) — see REFERRAL-FIELD-NAMING.md.
    const sId = f(r, 'sending_user_id');
    const rId = f(r, 'receiving_user_id');
    if (sId != null) {
      const agg = ensure(sId, {
        name: f(r, 'sending_user_name'),
        facility: f(r, 'sending_facility'),
        speciality: f(r, 'sending_speciality'),
      });
      agg.score += 1;
    }
    if (rId != null) {
      const agg = ensure(rId, {
        name: f(r, 'receiving_user_name'),
        facility: f(r, 'receiving_facility'),
        speciality: f(r, 'receiving_speciality'),
      });
      agg.score += 1;
    }
  }
  return m;
}
const zeroUser = () => ({ name: '', facility: '', speciality: '', score: 0 });

function facilityCount(rows) {
  const m = new Map();
  for (const r of rows) {
    const fac = f(r, 'receiving_facility');
    if (!fac) continue;
    m.set(fac, (m.get(fac) || 0) + 1);
  }
  return m;
}

// =========================================================================
// Card subcomponents
// =========================================================================

const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

function InsightCard({ title, subtitle, items, color = 'teal', emptyHint }) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  return (
    <section className="insights-card">
      <header className="insights-card-header">
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </header>
      {items.length === 0 ? (
        <p className="insights-card-empty">{emptyHint || 'No data.'}</p>
      ) : (
        <div className="insights-barlist">
          {items.map((it, i) => {
            const pct = (it.count / max) * 100;
            return (
              <div key={`${it.label}-${i}`} className="collab-barlist-row insights-row">
                <div className="collab-barlist-label">
                  {it.userId ? (
                    <PersonContactHover userId={it.userId}>{it.label}</PersonContactHover>
                  ) : (
                    <span title={it.label}>{it.label}</span>
                  )}
                  {it.sub && <div className="insights-row-sub">{it.sub}</div>}
                </div>
                <div className="collab-barlist-track">
                  <div className={`collab-barlist-fill collab-bar-${color}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="collab-barlist-count">{fmtInt(it.count)}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TrendCard({ title, subtitle, tone, rows, emptyHint, info }) {
  return (
    <section className={`insights-card insights-trend insights-trend-${tone}`}>
      <header className="insights-card-header">
        <h3>
          {title}
          {info && <InfoIcon text={info} />}
        </h3>
        {subtitle && <p>{subtitle}</p>}
      </header>
      {rows.length === 0 ? (
        <p className="insights-card-empty">{emptyHint}</p>
      ) : (
        <table className="insights-trend-table">
          <colgroup>
            <col />
            <col className="insights-col-num" />
            <col className="insights-col-num" />
            <col className="insights-col-num-wide" />
          </colgroup>
          <thead>
            <tr>
              <th>User</th>
              <th className="num">Prior</th>
              <th className="num">Current</th>
              <th className="num">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => {
              const arrow = u.delta >= 0 ? '▲' : '▼';
              const subline = [u.speciality, u.facility].filter(Boolean).join(' · ');
              return (
                <tr key={u.id}>
                  <td>
                    <div className="insights-row-main">
                      <PersonContactHover userId={u.id}>{u.name}</PersonContactHover>
                    </div>
                    <div className="insights-row-sub" title={subline}>{subline}</div>
                  </td>
                  <td className="num">{fmtInt(u.previousScore)}</td>
                  <td className="num">{fmtInt(u.currentScore)}</td>
                  <td className={`num insights-delta insights-delta-${tone}`}>{arrow} {fmtInt(Math.abs(u.delta))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FacilityTrendCard({ title, subtitle, tone, rows, info }) {
  return (
    <section className={`insights-card insights-trend insights-trend-${tone}`}>
      <header className="insights-card-header">
        <h3>
          {title}
          {info && <InfoIcon text={info} />}
        </h3>
        {subtitle && <p>{subtitle}</p>}
      </header>
      {rows.length === 0 ? (
        <p className="insights-card-empty">No facilities match.</p>
      ) : (
        <table className="insights-trend-table">
          <colgroup>
            <col />
            <col className="insights-col-num" />
            <col className="insights-col-num" />
            <col className="insights-col-num-wide" />
          </colgroup>
          <thead>
            <tr>
              <th>Facility</th>
              <th className="num">Prior</th>
              <th className="num">Current</th>
              <th className="num">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const arrow = r.delta >= 0 ? '▲' : '▼';
              return (
                <tr key={r.label}>
                  <td title={r.label}>{r.label}</td>
                  <td className="num">{fmtInt(r.previous)}</td>
                  <td className="num">{fmtInt(r.current)}</td>
                  <td className={`num insights-delta insights-delta-${tone}`}>{arrow} {fmtInt(Math.abs(r.delta))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CompletionCard({ rows }) {
  return (
    <section className="insights-card">
      <header className="insights-card-header">
        <h3>Referral Completion Rate</h3>
        <p>Closed/accepted referrals divided by total. Facilities with at least 5 referrals only.</p>
      </header>
      {rows.length === 0 ? (
        <p className="insights-card-empty">Not enough referral volume to rank.</p>
      ) : (
        <table className="insights-trend-table">
          <thead>
            <tr>
              <th>Facility</th>
              <th className="num">Closed</th>
              <th className="num">Total</th>
              <th className="num">Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map(r => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="num">{fmtInt(r.completed)}</td>
                <td className="num">{fmtInt(r.total)}</td>
                <td className="num"><strong>{(r.rate * 100).toFixed(1)}%</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// =========================================================================
// Spokes tab body
// =========================================================================
//
// Wrapped in its own component so the parent's render stays readable. Two
// section bands (USERS, then FACILITIES) each containing four cards in a
// 2x2 grid: top-three leaderboards + a drop-off / rising pair below.

function SpokesTabBody({
  spokeReady,
  spokeStats,
  hasCustomerScope,
  topSpokeUsersByLogins,
  topSpokeMessageSenders,
  topSpokeReferralSenders,
  topSpokeReferralCreators,
  topSpokeFacilitiesByLogins,
  topSpokeFacilitiesBySending,
  topSpokeFacilitiesByCreating,
  spokeUsersDropping,
  spokeUsersRising,
  spokeFacilitiesDeclining,
  spokeFacilitiesGrowing,
}) {
  if (!spokeReady) {
    return (
      <div className="insights-spokes-loading">
        <p>
          Loading spoke detection… <InfoIcon text={SPOKE_TAB_INFO} />
        </p>
        <p className="insights-card-empty">
          Building the union of every customer's facility IDs so we can
          identify non-customer activity. If this hangs, the
          <code> /api/customers/all-customer-facility-ids </code>
          endpoint may not be reachable for your role.
        </p>
      </div>
    );
  }

  return (
    <>
      <header className="insights-spokes-header">
        <h2>
          Spoke Activity
          <InfoIcon text={SPOKE_TAB_INFO} />
        </h2>
        <p>
          Free, non-customer accounts. Spoke activity is a leading indicator
          for sales — heavy spoke usage is a sales lead; spoke users that
          slip away mean we missed a window.
        </p>
        {hasCustomerScope && (
          <p className="insights-spokes-cohort-note">
            Scoped to <strong>this customer's spoke cohort</strong>: spoke
            users whose referrals (current or prior window) flow into the
            selected customer. Logins, messages, and trends below are
            restricted to that same set of users.
          </p>
        )}
        <div className="insights-spokes-stats">
          <span><strong>{spokeStats.sendingRefs.toLocaleString()}</strong> spoke-originated referrals</span>
          <span><strong>{spokeStats.creatingRefs.toLocaleString()}</strong> spoke-typed referrals</span>
          <span><strong>{spokeStats.loginRows.toLocaleString()}</strong> spoke login rollups</span>
          <span><strong>{spokeStats.messageRows.toLocaleString()}</strong> spoke message rollups</span>
        </div>
      </header>

      <h3 className="insights-spokes-band-title">Spoke Users</h3>
      <div className="insights-grid">
        <InsightCard
          title="Top Spoke Users by Logins"
          subtitle="Free-account users logging in the most. Hot conversion candidates."
          items={topSpokeUsersByLogins}
          color="blue"
          emptyHint="No spoke logins in this window. (Logins may not be cached yet.)"
        />
        <InsightCard
          title="Top Spoke Users by Messages Sent"
          subtitle="Sum of messages sent (referral, vmail, vchat, vrounds, vtransfer)."
          items={topSpokeMessageSenders}
          color="teal"
          emptyHint="No spoke messages in this window."
        />
        <InsightCard
          title="Top Spoke Users by Referrals Sent"
          subtitle="Spoke physicians referring patients OUT (sending side — patient source)."
          items={topSpokeReferralSenders}
          color="green"
          emptyHint="No spoke referrals in this window."
        />
        <InsightCard
          title="Top Spoke Users by Referrals Created"
          subtitle="Spoke staff who keyed referrals into VitalEngine (the typist, distinct from the referring physician)."
          items={topSpokeReferralCreators}
          color="green"
          emptyHint="No spoke-created referrals in this window."
        />
        <TrendCard
          title="Spoke Users Slipping"
          subtitle="Free-account users whose engagement has dropped 50%+. Last chance to convert before they're gone."
          tone="bad"
          rows={spokeUsersDropping}
          emptyHint="No spoke users fit the drop-off pattern in this window."
          info={SPOKE_DROP_INFO}
        />
        <TrendCard
          title="Spoke Users Heating Up"
          subtitle="Big jumps in spoke engagement — including brand-new adopters. Sales should reach out."
          tone="good"
          rows={spokeUsersRising}
          emptyHint="No standout spoke climbers this window."
          info={SPOKE_RISE_INFO}
        />
      </div>

      <h3 className="insights-spokes-band-title">Spoke Facilities</h3>
      <div className="insights-grid">
        <InsightCard
          title="Top Spoke Facilities by Logins"
          subtitle="Non-customer facilities with the most user activity."
          items={topSpokeFacilitiesByLogins}
          color="blue"
          emptyHint="No spoke logins in this window."
        />
        <InsightCard
          title="Top Spoke Facilities by Referrals Sent"
          subtitle="Non-customer facilities sending the most referrals (patient source)."
          items={topSpokeFacilitiesBySending}
          color="green"
          emptyHint="No spoke-originated referrals in this window."
        />
        <InsightCard
          title="Top Spoke Facilities by Referrals Created"
          subtitle="Non-customer facilities whose staff typed the most referrals."
          items={topSpokeFacilitiesByCreating}
          color="teal"
          emptyHint="No spoke-created referrals in this window."
        />
        <FacilityTrendCard
          title="Spoke Facilities Losing Volume"
          subtitle="Spoke facilities sending fewer referrals than prior. Worth a check-in."
          tone="bad"
          rows={spokeFacilitiesDeclining}
          info={FACILITY_DECLINE_INFO}
        />
        <FacilityTrendCard
          title="Spoke Facilities Growing Volume"
          subtitle="Spoke facilities sending more referrals than prior — building referral patterns through VitalEngine."
          tone="good"
          rows={spokeFacilitiesGrowing}
          info={FACILITY_GROW_INFO}
        />
      </div>
    </>
  );
}

// =========================================================================
// Actions tab body
// =========================================================================
//
// Top-10 ranked, AI-curated to-do list. The AI receives every visible
// trend row across all 8 trend tables and is asked to pick the 10 most
// important things a CS rep should do this week — not one-per-row.
// A CS rep can't realistically tackle 60+ actions, and asking the model
// for one-per-row was both unhelpful and was timing out at the nginx
// 60s ceiling.
//
// We don't repeat the underlying trend rows here — those already live
// on the People / Facilities / Spokes tabs. The Actions tab is purely
// the prioritized worklist.

function ActionsTabBody({ actionsLoading, actionsError, actionsMeta, actions }) {
  // Three states for the body: never-loaded (null), loading (spinner),
  // loaded (list — possibly empty).
  const haveResponse = Array.isArray(actions);
  const list = haveResponse ? actions : [];

  return (
    <>
      <header className="insights-actions-header">
        <h2>Top 10 Actions This Week</h2>
        <p>
          AI-curated worklist — the 10 highest-impact next steps from across the People,
          Facilities, and Spokes tabs, ranked by priority. Underlying trend data lives
          on the other tabs; this is the &quot;what should I do today?&quot; view.
        </p>
        <div className="insights-actions-meta">
          {actionsLoading && (
            <span className="insights-actions-meta-loading">
              <span className="insights-actions-shimmer" /> Triaging trends and picking the top 10…
            </span>
          )}
          {!actionsLoading && actionsMeta && haveResponse && (
            <span>
              <strong>{list.length}</strong> action{list.length === 1 ? '' : 's'} · generated by{' '}
              <strong>{actionsMeta.provider} {actionsMeta.model}</strong>
              {actionsMeta.generatedAt && (
                <> · {new Date(actionsMeta.generatedAt).toLocaleTimeString()}</>
              )}
            </span>
          )}
          {!actionsLoading && actionsMeta?.parseWarning && (
            <span className="insights-actions-warning">
              ⚠ {actionsMeta.parseWarning}
            </span>
          )}
          {actionsError && (
            <span className="insights-actions-error">
              {actionsError}
            </span>
          )}
        </div>
      </header>

      {actionsLoading && !haveResponse && (
        <ol className="insights-actions-list">
          {[1,2,3,4,5,6,7,8,9,10].map(n => (
            <li key={n} className="insights-action-card insights-action-card-loading">
              <div className="insights-action-rank">{n}</div>
              <div className="insights-action-body">
                <span className="insights-actions-shimmer insights-actions-shimmer-line" />
                <span className="insights-actions-shimmer insights-actions-shimmer-line short" />
                <span className="insights-actions-shimmer insights-actions-shimmer-line" />
              </div>
            </li>
          ))}
        </ol>
      )}

      {!actionsLoading && haveResponse && list.length === 0 && (
        <div className="insights-actions-empty-state">
          <h3>No actions to take this week.</h3>
          <p>
            The AI reviewed every trend row across all tabs and didn't find anything
            worth flagging — either everything is healthy, or the date range / filter
            is too narrow to surface meaningful change. Try widening the window or
            removing the customer filter.
          </p>
        </div>
      )}

      {haveResponse && list.length > 0 && (
        <ol className="insights-actions-list">
          {list.map((a, idx) => (
            <ActionCard key={idx} action={a} fallbackRank={idx + 1} />
          ))}
        </ol>
      )}
    </>
  );
}

function ActionCard({ action, fallbackRank }) {
  const rank = action?.rank ?? fallbackRank;
  const tone = GROUP_TONE[action?.groupKey] || 'neutral';
  return (
    <li className={`insights-action-card insights-action-card-${tone}`}>
      <div className="insights-action-rank">{rank}</div>
      <div className="insights-action-body">
        <div className="insights-action-card-head">
          {action?.category && (
            <span className={`insights-action-chip insights-action-chip-${tone}`}>{action.category}</span>
          )}
          {action?.subject && (
            <span className="insights-action-subject" title={action.subject}>{action.subject}</span>
          )}
        </div>
        <p className="insights-action-text">{action?.action}</p>
        <div className="insights-action-foot">
          {action?.metric && (
            <span className="insights-action-metric" title="Metric driving this priority">
              {action.metric}
            </span>
          )}
          {action?.why && (
            <span className="insights-action-why" title="Why this is a top priority">
              {action.why}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

export default Insights;
